import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Search, Bell, BellOff, Trash2, Users, User, Clock, Calendar, Check, X } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { getStatusColor, getStatusLabel, getPriorityColor, formatDateTime, cn } from '../lib/utils';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useSmartRefresh, WS_EVENTS } from '../hooks/useSmartRefresh';

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const { user, hasRole } = useAuth();
  const { theme } = useTheme();

  // Check if user can create/manage tasks (responsable or admin only)
  const canManageTasks = hasRole(['admin', 'responsable']);
  const isAdmin = hasRole(['admin']);

  const [newTask, setNewTask] = useState({
    titre: '',
    description: '',
    priorite: 'moyenne',
    alerte: false,
    assigned_to: [],
    assign_to_all: false,
    date_echeance: '',
    heure_echeance: '',
    notify_assignees: true
  });

  useEffect(() => {
    fetchTasks();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = tasks.filter(t =>
        `${t.titre} ${t.description}`.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredTasks(filtered);
    } else {
      setFilteredTasks(tasks);
    }
  }, [searchTerm, tasks]);

  const fetchTasks = useCallback(async () => {
    try {
      const response = await api.get('/tasks');
      setTasks(response.data);
      setFilteredTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Erreur lors du chargement des tâches');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/messages/users/search?q=');
      setAllUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Smart refresh: WebSocket + polling fallback 30s
  useSmartRefresh(fetchTasks, {
    wsEvents: [WS_EVENTS.TASK_UPDATED, WS_EVENTS.TASK_CREATED, WS_EVENTS.TASK_DELETED],
    fallbackInterval: 30000
  });

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const taskData = {
        titre: newTask.titre,
        description: newTask.description,
        priorite: newTask.priorite,
        alerte: newTask.alerte,
        heure_echeance: newTask.heure_echeance || null,
        notify_assignees: newTask.notify_assignees
      };

      // Gérer les assignations
      if (newTask.assign_to_all) {
        taskData.assigned_to = ['all'];
      } else if (newTask.assigned_to.length > 0) {
        taskData.assigned_to = newTask.assigned_to;
      }

      // Convert empty string to null for date
      if (newTask.date_echeance && newTask.date_echeance.trim() !== '') {
        taskData.date_echeance = new Date(newTask.date_echeance).toISOString();
      } else {
        taskData.date_echeance = null;
      }
      
      await api.post('/tasks', taskData);
      toast.success('Tâche créée avec succès');
      if (newTask.notify_assignees && (newTask.assigned_to.length > 0 || newTask.assign_to_all)) {
        toast.info('Notifications envoyées aux utilisateurs assignés');
      }
      setDialogOpen(false);
      resetForm();
      fetchTasks();
    } catch (error) {
      console.error('Error creating task:', error);
      const errorMsg = error?.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Erreur lors de la création');
    }
  };

  const resetForm = () => {
    setNewTask({
      titre: '',
      description: '',
      priorite: 'moyenne',
      alerte: false,
      assigned_to: [],
      assign_to_all: false,
      date_echeance: '',
      heure_echeance: '',
      notify_assignees: true
    });
  };

  const handleUpdateStatus = async (taskId, newStatus) => {
    try {
      await api.put(`/tasks/${taskId}`, { statut: newStatus });
      toast.success('Statut mis à jour');
      fetchTasks();
    } catch (error) {
      console.error('Error updating status:', error);
      const errorMsg = error?.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Erreur lors de la mise à jour');
    }
  };

  const handleToggleAlert = async (task) => {
    try {
      await api.put(`/tasks/${task.id}`, { alerte: !task.alerte });
      toast.success(task.alerte ? 'Alerte désactivée' : 'Alerte activée');
      fetchTasks();
    } catch (error) {
      console.error('Error toggling alert:', error);
      const errorMsg = error?.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!confirm('Supprimer cette tâche ?')) return;
    try {
      await api.delete(`/tasks/${taskId}`);
      toast.success('Tâche supprimée');
      fetchTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
      const errorMsg = error?.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Erreur lors de la suppression');
    }
  };

  const toggleUserSelection = (userId) => {
    setNewTask(prev => {
      const isSelected = prev.assigned_to.includes(userId);
      if (isSelected) {
        return { ...prev, assigned_to: prev.assigned_to.filter(id => id !== userId) };
      } else {
        return { ...prev, assigned_to: [...prev.assigned_to, userId], assign_to_all: false };
      }
    });
  };

  const getPriorityBadgeClass = (priorite) => {
    switch (priorite) {
      case 'haute':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'moyenne':
        return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'basse':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      default:
        return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
    }
  };

  const formatEcheance = (task) => {
    if (!task.date_echeance) return null;
    const date = new Date(task.date_echeance);
    const dateStr = date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    if (task.heure_echeance) {
      return `${dateStr} à ${task.heure_echeance}`;
    }
    return dateStr;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-zinc-800 rounded-full"></div>
          <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-red-500 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 px-1 md:p-0 pb-24 md:pb-0" data-testid="tasks-page">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className={cn("absolute left-2.5 top-1/2 transform -translate-y-1/2", theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')} size={16} />
          <Input
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={cn(
              "pl-8 h-9 text-sm",
              theme === 'dark' ? 'bg-zinc-900 border-white/10 text-white placeholder:text-zinc-500' : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
            )}
            data-testid="search-input"
          />
        </div>
        
        {/* Only show create button for responsable and admin */}
        {canManageTasks && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button variant="brand" data-testid="add-task-button" className="h-9 w-9 p-0 shrink-0">
                <Plus size={16} />
              </Button>
            </DialogTrigger>
            <DialogContent className={cn(
              "border max-w-lg max-h-[90vh] overflow-y-auto",
              theme === 'dark' ? 'bg-black border-white/10' : 'bg-white border-gray-200'
            )}>
              <DialogHeader>
                <DialogTitle className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Nouvelle tâche</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateTask} className="space-y-4">
                <div>
                  <Label htmlFor="titre" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Titre</Label>
                  <Input
                    id="titre"
                    value={newTask.titre}
                    onChange={(e) => setNewTask({...newTask, titre: e.target.value})}
                    required
                    className={cn(
                      theme === 'dark' ? 'bg-zinc-900 border-white/10 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                    )}
                    data-testid="input-titre"
                  />
                </div>
                <div>
                  <Label htmlFor="description" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Description</Label>
                  <Textarea
                    id="description"
                    value={newTask.description}
                    onChange={(e) => setNewTask({...newTask, description: e.target.value.slice(0, 600)})}
                    maxLength={600}
                    rows={3}
                    required
                    className={cn(
                      theme === 'dark' ? 'bg-zinc-900 border-white/10 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                    )}
                    data-testid="input-description"
                  />
                  <span className={cn("text-xs mt-1 block text-right", newTask.description.length >= 600 ? 'text-red-400' : theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')}>
                    {newTask.description.length}/600
                  </span>
                </div>
                <div>
                  <Label htmlFor="priorite" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Priorité</Label>
                  <Select
                    value={newTask.priorite}
                    onValueChange={(value) => setNewTask({...newTask, priorite: value})}
                  >
                    <SelectTrigger className={cn(
                      theme === 'dark' ? 'bg-zinc-900 border-white/10 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                    )} data-testid="select-priorite">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={cn(
                      theme === 'dark' ? 'bg-black border-white/10' : 'bg-white border-gray-200'
                    )}>
                      <SelectItem value="haute">Haute</SelectItem>
                      <SelectItem value="moyenne">Moyenne</SelectItem>
                      <SelectItem value="basse">Basse</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Assignation */}
                <div>
                  <Label className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Assigner à</Label>
                  
                  {/* Option tout le monde */}
                  <button
                    type="button"
                    onClick={() => setNewTask(prev => ({ 
                      ...prev, 
                      assign_to_all: !prev.assign_to_all,
                      assigned_to: [] 
                    }))}
                    className={cn(
                      "w-full mt-2 flex items-center gap-3 p-3 rounded-lg border transition-colors",
                      newTask.assign_to_all 
                        ? "border-red-500 bg-red-500/10" 
                        : theme === 'dark' ? 'border-white/10 bg-zinc-900 hover:bg-zinc-800' : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                    )}
                  >
                    <Users size={18} className={newTask.assign_to_all ? "text-red-500" : (theme === 'dark' ? "text-zinc-400" : "text-gray-500")} />
                    <span className={cn("flex-1 text-left", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                      Tout le monde
                    </span>
                    {newTask.assign_to_all && <Check size={18} className="text-red-500" />}
                  </button>

                  {/* Liste des utilisateurs */}
                  {!newTask.assign_to_all && (
                    <div className={cn(
                      "mt-2 max-h-40 overflow-y-auto rounded-lg border",
                      theme === 'dark' ? 'border-white/10 bg-zinc-900' : 'border-gray-200 bg-gray-50'
                    )}>
                      {allUsers.filter(u => u.id !== user?.id).map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => toggleUserSelection(u.id)}
                          className={cn(
                            "w-full flex items-center gap-3 p-2 border-b last:border-b-0 transition-colors",
                            newTask.assigned_to.includes(u.id)
                              ? "bg-red-500/10"
                              : theme === 'dark' ? 'border-white/5 hover:bg-zinc-800' : 'border-gray-100 hover:bg-gray-100'
                          )}
                        >
                          <div className={cn(
                            "w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium",
                            theme === 'dark' ? 'bg-zinc-700 text-white' : 'bg-gray-200 text-gray-700'
                          )}>
                            {u.prenom?.[0]}{u.nom?.[0]}
                          </div>
                          <span className={cn("flex-1 text-left text-sm", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                            {u.prenom} {u.nom}
                          </span>
                          {newTask.assigned_to.includes(u.id) && <Check size={16} className="text-red-500" />}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Résumé des sélections */}
                  {(newTask.assigned_to.length > 0 || newTask.assign_to_all) && (
                    <div className={cn(
                      "mt-2 p-2 rounded-lg text-sm",
                      theme === 'dark' ? 'bg-red-500/10 text-red-400' : 'bg-red-50 text-red-600'
                    )}>
                      {newTask.assign_to_all 
                        ? "Assigné à tout le monde" 
                        : `${newTask.assigned_to.length} utilisateur(s) sélectionné(s)`}
                    </div>
                  )}
                </div>

                {/* Date et heure d'échéance */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="date_echeance" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>
                      <Calendar size={14} className="inline mr-1" />
                      Date d'échéance
                    </Label>
                    <Input
                      id="date_echeance"
                      type="date"
                      value={newTask.date_echeance}
                      onChange={(e) => setNewTask({...newTask, date_echeance: e.target.value})}
                      className={cn(
                        'mt-1',
                        theme === 'dark' ? 'bg-zinc-900 border-white/10 text-white [&::-webkit-calendar-picker-indicator]:invert' : 'bg-gray-50 border-gray-200 text-gray-900'
                      )}
                      data-testid="input-date"
                    />
                  </div>
                  <div>
                    <Label htmlFor="heure_echeance" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>
                      <Clock size={14} className="inline mr-1" />
                      Heure d'échéance
                    </Label>
                    <Input
                      id="heure_echeance"
                      type="time"
                      value={newTask.heure_echeance}
                      onChange={(e) => setNewTask({...newTask, heure_echeance: e.target.value})}
                      className={cn(
                        'mt-1',
                        theme === 'dark' ? 'bg-zinc-900 border-white/10 text-white [&::-webkit-calendar-picker-indicator]:invert' : 'bg-gray-50 border-gray-200 text-gray-900'
                      )}
                      data-testid="input-heure"
                    />
                  </div>
                </div>
                
                {/* Alert toggle */}
                <div className={cn(
                  "flex items-center gap-3 p-3 rounded-lg border",
                  theme === 'dark' ? 'border-white/10 bg-zinc-900' : 'border-gray-200 bg-gray-50'
                )}>
                  <input
                    type="checkbox"
                    id="alerte"
                    checked={newTask.alerte}
                    onChange={(e) => setNewTask({...newTask, alerte: e.target.checked})}
                    className="w-5 h-5 rounded border-zinc-600 bg-zinc-700 text-red-500 focus:ring-red-500"
                    data-testid="checkbox-alerte"
                  />
                  <div className="flex-1">
                    <Label htmlFor="alerte" className={cn(
                      "cursor-pointer flex items-center gap-2",
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    )}>
                      <Bell size={18} className="text-red-500" />
                      Mettre en alerte
                    </Label>
                    <p className={cn("text-xs mt-1", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>
                      La tâche apparaîtra sur le tableau de bord
                    </p>
                  </div>
                </div>

                {/* Notification toggle */}
                {(newTask.assigned_to.length > 0 || newTask.assign_to_all) && (
                  <div className={cn(
                    "flex items-center gap-3 p-3 rounded-lg border",
                    theme === 'dark' ? 'border-white/10 bg-zinc-900' : 'border-gray-200 bg-gray-50'
                  )}>
                    <input
                      type="checkbox"
                      id="notify"
                      checked={newTask.notify_assignees}
                      onChange={(e) => setNewTask({...newTask, notify_assignees: e.target.checked})}
                      className="w-5 h-5 rounded border-zinc-600 bg-zinc-700 text-red-500 focus:ring-red-500"
                      data-testid="checkbox-notify"
                    />
                    <div className="flex-1">
                      <Label htmlFor="notify" className={cn(
                        "cursor-pointer flex items-center gap-2",
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      )}>
                        {newTask.notify_assignees ? <Bell size={18} className="text-blue-500" /> : <BellOff size={18} className="text-zinc-500" />}
                        Notifier les utilisateurs
                      </Label>
                      <p className={cn("text-xs mt-1", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>
                        Envoyer une notification aux personnes assignées
                      </p>
                    </div>
                  </div>
                )}
                
                <Button type="submit" variant="brand" className="w-full" data-testid="submit-task">
                  Créer la tâche
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Tasks list */}
      <div className="space-y-3">
        {filteredTasks.length === 0 ? (
          <div className={cn(
            "text-center py-12 rounded-xl border",
            theme === 'dark' ? 'bg-zinc-900/50 border-white/10' : 'bg-white border-gray-200'
          )}>
            <p className={theme === 'dark' ? 'text-zinc-500' : 'text-gray-500'}>Aucune tâche trouvée</p>
          </div>
        ) : (
          filteredTasks.map((task) => (
            <div
              key={task.id}
              className={cn(
                "p-4 rounded-xl border transition-all",
                theme === 'dark' 
                  ? 'bg-zinc-900/50 border-white/10 hover:border-white/20' 
                  : 'bg-white border-gray-200 hover:border-gray-300',
                task.alerte && 'border-red-500/50'
              )}
              data-testid={`task-${task.id}`}
            >
              {/* Header: titre + actions */}
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className={cn(
                    "font-semibold break-words",
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  )}>
                    {task.titre}
                  </h3>
                  <span className={cn(
                    "px-2 py-0.5 text-xs rounded-full border flex-shrink-0",
                    getPriorityBadgeClass(task.priorite)
                  )}>
                    {task.priorite}
                  </span>
                  {task.alerte && (
                    <Bell size={14} className="text-red-500 flex-shrink-0" />
                  )}
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Select
                    value={task.statut}
                    onValueChange={(value) => handleUpdateStatus(task.id, value)}
                  >
                    <SelectTrigger className={cn(
                      "w-[110px] h-8 text-xs",
                      theme === 'dark' ? 'bg-zinc-800 border-white/10' : 'bg-gray-50 border-gray-200'
                    )}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={cn(
                      theme === 'dark' ? 'bg-black border-white/10' : 'bg-white border-gray-200'
                    )}>
                      <SelectItem value="en_attente">En attente</SelectItem>
                      <SelectItem value="en_cours">En cours</SelectItem>
                      <SelectItem value="terminee">Terminée</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {canManageTasks && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleAlert(task)}
                        className={cn(
                          "h-8 w-8 p-0",
                          task.alerte ? "text-red-500" : (theme === 'dark' ? "text-zinc-500" : "text-gray-400")
                        )}
                        title={task.alerte ? "Désactiver l'alerte" : "Activer l'alerte"}
                      >
                        {task.alerte ? <Bell size={16} /> : <BellOff size={16} />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteTask(task.id)}
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-400"
                        data-testid={`delete-task-${task.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </>
                  )}
                </div>
              </div>
              
              {/* Description */}
              <p className={cn(
                "text-sm mt-2 break-words whitespace-pre-wrap",
                theme === 'dark' ? 'text-zinc-400' : 'text-gray-600'
              )}>
                {task.description}
              </p>
              
              {/* Metadata */}
              <div className="flex flex-wrap items-center gap-3 mt-3 text-xs">
                {(task.assigned_to_names?.length > 0 || task.is_assigned_to_all) && (
                  <span className={cn(
                    "flex items-center gap-1",
                    theme === 'dark' ? 'text-zinc-500' : 'text-gray-500'
                  )}>
                    <User size={12} className="flex-shrink-0" />
                    <span className="break-words">
                      {task.is_assigned_to_all 
                        ? "Tout le monde" 
                        : task.assigned_to_names?.join(', ')}
                    </span>
                  </span>
                )}
                
                {task.date_echeance && (
                  <span className={cn(
                    "flex items-center gap-1",
                    new Date(task.date_echeance) < new Date() && task.statut !== 'terminee'
                      ? "text-red-500"
                      : theme === 'dark' ? 'text-zinc-500' : 'text-gray-500'
                  )}>
                    <Calendar size={12} className="flex-shrink-0" />
                    {formatEcheance(task)}
                  </span>
                )}
                
                <span className={cn(
                  theme === 'dark' ? 'text-zinc-600' : 'text-gray-400'
                )}>
                  Par {task.created_by_nom}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
